<script setup lang="ts">
    import { getConfig, klinikenApi } from "@/api";
    import LockGreyIcon from "@/assets/lock_grey.svg";
    import UnlockGreyIcon from "@/assets/unlock_grey.svg";
    import ModalContent from "@/components/generic/ModalContent.vue";
    import { CellContentWidth } from "@/components/KTable/helpers";
    import KTable from "@/components/KTable/KTable.vue";
    import { Column, KTableAction, TableValue, TableValues } from "@/components/KTable/types";
    import { useStore } from "@/store";
    import { SparrValue } from "@/store/modules/recept/helpers";
    import CellLakemedel from "@/tabs/Receptforskrivning/cells/CellLakemedel.vue";
    import CellReceptIkoner from "@/tabs/Receptforskrivning/cells/CellReceptIkoner.vue";
    import Receptforskrivning from "@/tabs/Receptforskrivning/Forskrivning/Receptforskrivning.vue";
    import HavSparr from "@/tabs/Receptforskrivning/HavSparr.vue";
    import {
        checkNllLocks,
        findNllLockBehandlingsorsak,
        findNllLockSekretessVardnadshavare,
        inactivateSparr,
        openNLLErrorDialog,
        sendSparr,
    } from "@/tabs/Receptforskrivning/helpers";
    import SparrForskrivning from "@/tabs/Receptforskrivning/SparrForskrivning.vue";
    import AvslutaForskrivning from "@/tabs/Receptforskrivning/Start/AvslutaForskrivning.vue";
    import ReceptforskrivningListExpanded from "@/tabs/Receptforskrivning/Start/List/ReceptforskrivningListExpanded.vue";
    import MakuleraForskrivning from "@/tabs/Receptforskrivning/Start/MakuleraForskrivning.vue";
    import {
        AvslutaMakulera,
        Forskrivningsstatus,
        isNLLPatientInfo,
        ReceptforskrivningDisplay,
        Sparr,
    } from "@/tabs/Receptforskrivning/types";
    import { flashMessage, flashWarning } from "@/utils";
    import { getAge, hasPersonnummer } from "@/utils/patient/patientData";
    import { hasForskrivarkod } from "@/utils/user";
    import emitter from "tiny-emitter/instance";
    import { computed, ref, toRefs } from "vue";

    const store = useStore();

    const props = defineProps<{
        title?: string;
        loadingMessage: string;
        loading: boolean;
        forskrivningar: TableValues;
        type: "historiska" | "aktiva" | "lokala" | "papper";
    }>();

    const { type } = toRefs(props);

    const emit = defineEmits<{
        (event: "rowClick", payload: { data: ReceptforskrivningDisplay }): void;
    }>();

    const tableRef = ref();

    const showMakuleraForskrivning = ref(false);
    const showAvslutaForskrivning = ref(false);
    const selectedForskrivning = ref<ReceptforskrivningDisplay | null>(null);

    const currentProfile = computed(() => store.getters["userData/currentProfile"]);
    const patient = computed(() => store.getters["patientData/patient"]);

    const getExpandedRows = () => tableRef?.value?.expandedRows;

    defineExpose({
        getExpandedRows,
    });

    const columns = computed<Column[]>(() => [
        {
            field: "ikoner",
            header: "",
            frozen: true,
            minWidth: CellContentWidth.tiny,
            limitWidth: true,
            bodyTemplate: CellReceptIkoner,
        },
        {
            field: "lakemedelInfo",
            header: "Läkemedel",
            minWidth: CellContentWidth.m,
            bodyTemplate: CellLakemedel,
            frozen: true,
            truncate: true,
            lines: 2,
        },
        {
            field: "styrka",
            header: "Styrka",
            minWidth: CellContentWidth.xxxs,
            limitWidth: true,
            truncate: true,
            lines: 2,
        },
        { field: "substans", header: "Substans", minWidth: CellContentWidth.s, limitWidth: true },
        { field: "atc", header: "ATC", minWidth: CellContentWidth.xxxs, limitWidth: true },
        { field: "form", header: "Form", limitWidth: true },
        {
            field: "doseringsanvisning",
            header: "Doseringsanvisning",
            truncate: true,
            lines: 2,
            minWidth: CellContentWidth.xl,
        },
        type.value === "historiska" || type.value === "papper"
            ? { field: "forskrivningsstatus", header: "Status", minWidth: CellContentWidth.xs, limitWidth: true }
            : { field: "uttag_kvar", header: "Uttag kvar", minWidth: CellContentWidth.xxxs, limitWidth: true },
        {
            field: "utfardandedatumDate",
            header: type.value === "papper" ? "Datum" : "Utfärdat",
            minWidth: CellContentWidth.xxs,
            limitWidth: true,
        },
    ]);

    const closeAvslutaMakulera = () => {
        showAvslutaForskrivning.value = false;
        showMakuleraForskrivning.value = false;
        selectedForskrivning.value = null;
    };

    const submitAvslutaMakulera = async ({
        data,
        forskrivning_id,
        parallell_id,
        typ,
    }: {
        data: any;
        forskrivning_id: string;
        parallell_id: string | null;
        typ: keyof typeof AvslutaMakulera;
    }) => {
        data = {
            ...data,
            forskrivning_id,
            parallell_id,
            typ: AvslutaMakulera[typ],
        };
        try {
            await klinikenApi.post(
                `/nll/forskrivningar/${typ}/`,
                data,
                getConfig({ headers: store.getters["nll/headers"] })
            );

            if (parallell_id) {
                flashMessage(`Förskrivningar ${typ}de`);
            } else {
                flashMessage(`Förskrivningen ${typ}d`);
            }

            emitter.emit("refreshReceptforskrivningar");

            closeAvslutaMakulera();
        } catch (error: any) {
            let description;

            if (parallell_id) {
                description = `Kunde inte ${typ} förskrivningarna.`;
            } else {
                description = `Kunde inte ${typ} förskrivningen.`;
            }

            openNLLErrorDialog({ error, description });
        }
    };

    const canSparraForskrivning = (data: unknown, actionType?: "papper" | "elektroniskt") => {
        if (!isNLLPatientInfo(store.getters["nll/patientinfo"])) {
            return false;
        }

        if (!store.getters["userData/currentProfile"].personalPrescriptionCode) {
            return false;
        }

        const selectedForskrivning = data as ReceptforskrivningDisplay;

        if (selectedForskrivning.atc === undefined || selectedForskrivning.dosdispensering) {
            return false;
        }

        if (actionType === "papper" && selectedForskrivning.aktuellt_format === "Elektroniskt") {
            return false;
        }

        if (actionType === "elektroniskt" && selectedForskrivning.aktuellt_format === "Papper") {
            return false;
        }

        const locks = checkNllLocks({
            sparrar: (data as ReceptforskrivningDisplay)?.sparrar,
        });

        // Spärra förskrivning ska visas på patienten under 18 år så länge inte både behandlingsorsak och vårdnadshavare är spärrade
        const age = getAge({ dateOfBirth: store.getters["patientData/dateOfBirth"] });

        if (age && age < 18) {
            // Är det papperrecept går det inte att spärra behandlingsorsak
            if (selectedForskrivning.aktuellt_format === "Papper") {
                return !locks?.sekretessVardnadshavare;
            }
            return !locks?.behandlingsorsak || !locks?.sekretessVardnadshavare;
        }

        return (
            !locks?.behandlingsorsak &&
            !locks?.sekretessVardnadshavare &&
            selectedForskrivning.aktuellt_format === "Elektroniskt"
        );
    };

    const actions: KTableAction[] = [
        {
            title: "Förnya förskrivning",
            show: (data) => {
                // Kriterier: personal med förskrivarkod, ej dosdispensering, ej handelsvara, ej avslutad eller makulerad,
                // ej om det någonsin varit pappersrecept, ej parallell förskrivning.
                return (
                    type.value !== "lokala" &&
                    hasForskrivarkod({ profile: currentProfile.value }) &&
                    hasPersonnummer({ personIdType: patient?.value?.personIdType }) &&
                    !(data as ReceptforskrivningDisplay).dosdispensering &&
                    !(data as ReceptforskrivningDisplay).isHandelsvara &&
                    ![Forskrivningsstatus.Avslutad, Forskrivningsstatus.Makulerad].includes(
                        (data as ReceptforskrivningDisplay).forskrivningsstatus
                    ) &&
                    (data as ReceptforskrivningDisplay).aktuellt_format === "Elektroniskt" &&
                    (data as ReceptforskrivningDisplay).typ_av_registreringsunderlag === "Elektroniskt" &&
                    !(data as ReceptforskrivningDisplay).parallell
                );
            },
            callback: async ({ data }) => {
                // Behöver hämta options innan rendering av formulär. Annars blir administreringsinstruktioner tom
                await store.dispatch("nll/fetchKodverkByLakemedel");

                const typedData = data as ReceptforskrivningDisplay;
                // display innehåller mer än det vi är intresserade av i formuläret vid förnya. Thus re-assignment
                const receptforskrivning: any = {
                    /*
                     * Ser till att val i administrering finns, eftersom vi inte vet om valda administrering var
                     * bland alternativen som är baserade på läkemedel
                     */
                    administrering_all: true,
                    startforpackning: false,
                    id: typedData.id,
                    visa_administrering: [],
                    // Används av BE vid förnyelse
                    forskrivning: typedData.id,
                    lakemedelId: typedData.lakemedelId,
                    nplId: typedData.nplId,
                    antal_uttag: typedData.antal_uttag,
                    forpackningar_per_uttag: typedData.forpackningar_per_uttag,
                    substitution_tillaten: typedData.substitution_tillaten,
                    annan_orsak: typedData.annan_orsak,
                    forskrivarens_kommentar: typedData.forskrivarens_kommentar,
                    formansvillkor_for_lakemedel_uppfyllda: typedData.formansvillkor_for_lakemedel_uppfyllda,
                    formansberattigad_patient: typedData.formansberattigad_patient,
                    forskrivning_enligt_smittskyddslagen: typedData.forskrivning_enligt_smittskyddslagen,
                    vid_behov: typedData.vid_behov,
                    obs_overstiger_maxdosering: typedData.obs_overstiger_maxdosering,
                    administreringsmetod: typedData.administreringsmetod,
                    administreringsmetod_annan: typedData.administreringsmetod_annan,
                    administreringsvag: typedData.administreringsvag,
                    administreringsvag_annan: typedData.administreringsvag_annan,
                    administreringsstalle: typedData.administreringsstalle,
                    administreringsstalle_annan: typedData.administreringsstalle_annan,
                    precisering_administreringsstalle: typedData.precisering_administreringsstalle,
                    precisering_administreringsstalle_annan: typedData.precisering_administreringsstalle_annan,
                    medicinteknisk_produkt: typedData.medicinteknisk_produkt,
                    medicinteknisk_produkt_annan: typedData.medicinteknisk_produkt_annan,
                    tidsenhet: typedData.tidsenhet,
                    behandlingsandamal: typedData.behandlingsandamal,
                    dosering: typedData.dosering,
                    tid_mellan_uttag: typedData.tid_mellan_uttag_varde,
                    behandlingsorsak: typedData.behandlingsorsak_kod,
                    annan_instruktion: typedData.annan_instruktion,
                    /**
                     * Data som behövs för att avgöra hur formuläret renderas (med information till användaren), men
                     * de ska INTE sättas som value vid inläsning och inte skickas med till BE
                     */
                    extraData: {
                        senaste_datum_for_avslut_av_behandling: typedData.senaste_datum_for_avslut_av_behandling,
                        senaste_datum_for_uppfoljning_av_behandling:
                            typedData.senaste_datum_for_uppfoljning_av_behandling,
                        sista_doseringsdag: typedData.sista_doseringsdag,
                    },
                };

                if (receptforskrivning.administreringsmetod) {
                    receptforskrivning.visa_administrering = [...receptforskrivning.visa_administrering, "metod"];
                }

                if (receptforskrivning.administreringsvag) {
                    receptforskrivning.visa_administrering = [...receptforskrivning.visa_administrering, "vag"];
                }

                if (receptforskrivning.administreringsstalle) {
                    receptforskrivning.visa_administrering = [...receptforskrivning.visa_administrering, "stalle"];
                }

                if (receptforskrivning.medicinteknisk_produkt) {
                    receptforskrivning.visa_administrering = [
                        ...receptforskrivning.visa_administrering,
                        "medicinteknisk_produkt",
                    ];
                }

                store.commit("receptforskrivning/addReceptforskrivning", { receptforskrivning });

                store.dispatch("tabs/openTab", {
                    component: Receptforskrivning,
                    props: {
                        receptforskrivningId: receptforskrivning.id,
                        fornya: true,
                    },
                });
            },
        },
        {
            title: "Spärra förskrivning",
            icon: LockGreyIcon,
            show: (data) => canSparraForskrivning(data, "elektroniskt"),
            callback: ({ data }) => {
                selectedForskrivning.value = data as ReceptforskrivningDisplay;
                sparrReceptforskrivning.value = data;
                showSparrModal.value = true;
            },
        },
        {
            title: "Spärra uttag pappersrecept",
            icon: LockGreyIcon,
            show: (data) => canSparraForskrivning(data, "papper"),
            callback: ({ data }) => {
                selectedForskrivning.value = data as ReceptforskrivningDisplay;
                sparrReceptforskrivning.value = data;
                showSparrModal.value = true;
            },
        },
        {
            title: "Häv spärr behandlingsorsak",
            icon: UnlockGreyIcon,
            show: (data) => {
                const selectedForskrivning = data as ReceptforskrivningDisplay;

                return (
                    store.getters["userData/currentProfile"].personalPrescriptionCode &&
                    checkNllLocks({ sparrar: (data as ReceptforskrivningDisplay)?.sparrar })?.behandlingsorsak &&
                    selectedForskrivning.atc !== undefined &&
                    !selectedForskrivning.dosdispensering &&
                    selectedForskrivning.aktuellt_format === "Elektroniskt"
                );
            },
            callback: ({ data }) => {
                const sparr = findNllLockBehandlingsorsak({ sparrar: (data as ReceptforskrivningDisplay)?.sparrar });

                if (!sparr) {
                    flashWarning("Ingen spärr av behandlingsorsak hittades.");
                    return;
                }

                selectedForskrivning.value = data as ReceptforskrivningDisplay;
                havSparrType.value = "BEHANDLINGSORSAK";
                havSparrId.value = sparr.id;
            },
        },
        {
            title: "Häv spärr vårdnadshavare",
            icon: UnlockGreyIcon,
            show: (data) => {
                const selectedForskrivning = data as ReceptforskrivningDisplay;

                return (
                    store.getters["userData/currentProfile"].personalPrescriptionCode &&
                    checkNllLocks({ sparrar: (data as ReceptforskrivningDisplay)?.sparrar })?.sekretessVardnadshavare &&
                    selectedForskrivning.atc !== undefined &&
                    !selectedForskrivning.dosdispensering
                );
            },
            callback: ({ data }) => {
                const sparr = findNllLockSekretessVardnadshavare({
                    sparrar: (data as ReceptforskrivningDisplay)?.sparrar,
                });

                if (!sparr) {
                    flashWarning("Ingen spärr av vårdnadshavare hittades.");

                    return;
                }

                selectedForskrivning.value = data as ReceptforskrivningDisplay;
                havSparrType.value = "SEKRETESS_VH";
                havSparrId.value = sparr.id;
            },
        },
        {
            title: "Avsluta förskrivning",
            callback: ({ data }: { data: unknown }) => {
                selectedForskrivning.value = data as ReceptforskrivningDisplay;
                showAvslutaForskrivning.value = true;
            },
            show: (item: unknown) => {
                const sentItem = item as ReceptforskrivningDisplay;

                // Kriterier: personal med förskrivarkod, aktiv förskrivning, läkemedel ej handelsvara, ej dosförskrivning, ej pappersrecept
                return (
                    store.getters["userData/currentProfile"].personalPrescriptionCode &&
                    ["Aktiv", "Slutexpedierad", "Parkerad"].includes(sentItem.forskrivningsstatus) &&
                    sentItem.atc !== undefined &&
                    !sentItem.dosdispensering &&
                    sentItem.aktuellt_format === "Elektroniskt" &&
                    // Patienten hade INTE personnummer vid förskrivningstillfället eller om vi INTE tittar på lokala recept
                    (!sentItem.patient_k3_hade_personnummer || type.value !== "lokala")
                );
            },
        },
        {
            title: "Makulera förskrivning",
            callback: ({ data }: { data: unknown }) => {
                selectedForskrivning.value = data as ReceptforskrivningDisplay;
                showMakuleraForskrivning.value = true;
            },
            show: (item: unknown) => {
                const sentItem = item as ReceptforskrivningDisplay;
                // Kriterier: personal med förskrivarkod, aktiv förskrivning, läkemedel ej handelsvara, ej dosförskrivning, ej om det finns uttag, ej pappersrecept
                return (
                    store.getters["userData/currentProfile"].personalPrescriptionCode &&
                    ["Aktiv", "Parkerad"].includes(sentItem.forskrivningsstatus) &&
                    sentItem.atc !== undefined &&
                    !sentItem.dosdispensering &&
                    sentItem.antal_uttag_kvar === sentItem.antal_uttag &&
                    sentItem.aktuellt_format === "Elektroniskt" &&
                    // Patienten hade INTE personnummer vid förskrivningstillfället eller om vi INTE tittar på lokala recept
                    (!sentItem.patient_k3_hade_personnummer || type.value !== "lokala")
                );
            },
        },
    ];

    const sparrReceptforskrivning = ref<any>(null);
    const showSparrModal = ref(false);

    const toggleShowSparrModal = () => {
        sparrReceptforskrivning.value = null;
        showSparrModal.value = !showSparrModal.value;
    };

    const onSubmitSparr = async (data: SparrValue) => {
        const receptforskrivning = sparrReceptforskrivning.value;
        sparrReceptforskrivning.value = null;

        await sendSparr({
            patient_id: store.getters["patientData/patient"]?.id,
            forskrivningskedja_id: receptforskrivning?.forskrivningskedja_id,
            sparr: data,
            headers: store.getters["nll/headers"],
        });

        showSparrModal.value = false;
        flashMessage("Spärr skapad.");
        emitter.emit("refreshReceptforskrivningar");
    };

    const havSparrType = ref<"BEHANDLINGSORSAK" | "SEKRETESS_VH" | null>(null);
    const havSparrId = ref<Sparr["id"] | null>(null);

    const onHavSparrSubmit = async (sparrId: string) => {
        try {
            await inactivateSparr({
                patient_id: store.getters["patientData/patient"]?.id,
                sparr_id: sparrId,
                headers: store.getters["nll/headers"],
            });

            havSparrType.value = null;
            havSparrId.value = null;

            flashMessage("Spärren hävd.");
            emitter.emit("refreshReceptforskrivningar");
        } catch (error) {
            console.error(error);
        }
    };

    const onHavSparrCancel = async () => {
        havSparrType.value = null;
        havSparrId.value = null;
    };

    const onRowClick = async ({ data }: { data: unknown }) => {
        selectedForskrivning.value = data as ReceptforskrivningDisplay;

        emit("rowClick", { data: data as ReceptforskrivningDisplay });
    };

    const onMenuClick = async ({ data }: { data: unknown }) => {
        selectedForskrivning.value = data as ReceptforskrivningDisplay;
    };

    const getRowVariant = ({ data }: { data: TableValue }) => {
        return checkNllLocks({ sparrar: (data as ReceptforskrivningDisplay)?.sparrar })?.integritet
            ? "warning"
            : "default";
    };
</script>

<template>
    <div class="receptforskrivning-list">
        <h4 v-if="title" class="title">{{ title }}</h4>
        <slot name="tags" />
        <KTable
            ref="tableRef"
            :name="`receptforskrivning-${type}`"
            :value="forskrivningar"
            :columns="columns"
            :theme="type === 'historiska' ? 'grey' : 'default'"
            :actions="actions"
            :expandableRows="true"
            :multipleExpandableRows="false"
            :expandOnRowClick="true"
            expandedRowVariant="full-framed"
            width="unlimited"
            :loadingMessage="loadingMessage"
            :loading="loading"
            @rowClick="onRowClick"
            :getRowVariant="getRowVariant"
            @menuClick="onMenuClick"
        >
            <template #expandedRow="{ data }">
                <ReceptforskrivningListExpanded :data="data" />
            </template>
        </KTable>
        <portal v-if="showSparrModal" to="modal">
            <SparrForskrivning
                @submit="onSubmitSparr"
                @cancel="toggleShowSparrModal"
                :receptforskrivning="selectedForskrivning"
            />
        </portal>
        <HavSparr
            :sparrId="havSparrId"
            :sparrType="havSparrType"
            :isPapper="selectedForskrivning?.aktuellt_format === 'Papper'"
            @submit="onHavSparrSubmit"
            @cancel="onHavSparrCancel"
        />
        <ModalContent
            title="Avsluta förskrivning"
            v-if="showAvslutaForskrivning"
            @close="closeAvslutaMakulera"
            showHeader
            showCloseButton
        >
            <AvslutaForskrivning
                :forskrivningId="selectedForskrivning?.id"
                :parallellId="selectedForskrivning?.parallell"
                :senasteDatumForAvslutAvBehandling="
                    selectedForskrivning?.senaste_datum_for_avslut_av_behandling ?? null
                "
                @close="closeAvslutaMakulera"
                @submit="submitAvslutaMakulera"
            />
        </ModalContent>
        <ModalContent
            title="Makulera förskrivning"
            v-if="showMakuleraForskrivning"
            @close="closeAvslutaMakulera"
            showHeader
            showCloseButton
        >
            <MakuleraForskrivning
                :forskrivningId="selectedForskrivning?.id"
                :parallellId="selectedForskrivning?.parallell"
                @close="closeAvslutaMakulera"
                @submit="submitAvslutaMakulera"
            />
        </ModalContent>
    </div>
</template>

<style lang="scss" scoped>
    @import "@/style/variables";

    .receptforskrivning-list {
        display: flex;
        flex-direction: column;
        gap: 1rem;

        .title {
            margin-bottom: 0;
            font-size: $h3-font-size;
            line-height: $h3-line-height;
            font-weight: $h3-font-weight;
            color: $h3-color;
        }
    }
</style>
